import { safeSessionStorage } from './safeStorage'
import {
  installChunkReloadRecoveryHandlers,
  isRecoverableChunkError,
  type ChunkRecoveryReason,
} from './chunkReloadRecovery'
import { isStaleAssetUrl } from './chunkReloadRecoveryCore'

const LAST_ERROR_KEY = 'taisen-board:last-client-error'
let lastPageShowPersisted: boolean | undefined

type LastClientError = {
  at: string
  type: 'error' | 'unhandledrejection' | 'chunk-recovery'
  message: string
  source?: string
  lineno?: number
  colno?: number
  stack?: string
  path: string
  visibilityState: DocumentVisibilityState
  persisted?: boolean
  reason?: ChunkRecoveryReason
  detail?: string
}

function errorLike(value: unknown): { message: string; stack?: string } {
  if (value instanceof Error) {
    return { message: value.message, stack: value.stack }
  }
  if (typeof value === 'string') {
    return { message: value }
  }
  try {
    return { message: JSON.stringify(value) }
  } catch {
    return { message: String(value) }
  }
}

function saveLastError(entry: LastClientError) {
  safeSessionStorage.setJSON(LAST_ERROR_KEY, entry)
}

function eventResourceUrl(event: Event): string | null {
  const target = event.target
  if (target instanceof HTMLScriptElement) return target.src
  if (target instanceof HTMLLinkElement) return target.href
  return null
}

if (typeof window !== 'undefined') {
  installChunkReloadRecoveryHandlers()

  window.addEventListener('pageshow', (event) => {
    lastPageShowPersisted = event.persisted
  })

  window.addEventListener('error', (event) => {
    const resourceUrl = eventResourceUrl(event)
    if (resourceUrl && isStaleAssetUrl(resourceUrl, window.location.origin)) return
    if (isRecoverableChunkError(event.error ?? event.message, window.location.origin)) return

    const fallback = errorLike(event.error ?? event.message)
    saveLastError({
      at: new Date().toISOString(),
      type: 'error',
      message: event.message || fallback.message,
      source: event.filename || undefined,
      lineno: event.lineno || undefined,
      colno: event.colno || undefined,
      stack: fallback.stack,
      path: window.location.pathname + window.location.search,
      visibilityState: document.visibilityState,
      persisted: lastPageShowPersisted,
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (isRecoverableChunkError(event.reason, window.location.origin)) return

    const reason = errorLike(event.reason)
    saveLastError({
      at: new Date().toISOString(),
      type: 'unhandledrejection',
      message: reason.message,
      stack: reason.stack,
      path: window.location.pathname + window.location.search,
      visibilityState: document.visibilityState,
      persisted: lastPageShowPersisted,
    })
  })
}
